import { HttpError } from "@/shared/api/HttpError";
import { serialize } from "object-to-formdata";

interface NamedParameters {
	endpoint: string;
	queryParams?: string;
	odataParams?: string;
	signal?: AbortSignal;
}

type FormPayload = Record<string, unknown>;

const isODataResult = (
	value: unknown,
): value is { "@odata.context": unknown; value: unknown } => {
	if (!value || typeof value !== "object") return false;
	return "@odata.context" in value && "value" in value;
};

const toFormDataEntryValue = (value: unknown): FormDataEntryValue => {
	if (value instanceof Blob) return value;
	if (typeof value === "string") return value;
	if (
		typeof value === "number" ||
		typeof value === "boolean" ||
		typeof value === "bigint"
	)
		return String(value);
	if (value instanceof Date) return value.toISOString();
	if (value == null) return "";
	try {
		return JSON.stringify(value);
	} catch {
		return "";
	}
};

const appendFormPayload = (
	formData: FormData,
	payload: FormPayload,
	arrayMode: "indexed" | "single" = "single",
) => {
	for (const [key, rawValue] of Object.entries(payload)) {
		if (rawValue === undefined) continue;
		if (Array.isArray(rawValue)) {
			if (arrayMode === "indexed") {
				rawValue.forEach((entry, index) => {
					formData.append(`${key}[${index}]`, toFormDataEntryValue(entry));
				});
				continue;
			}
			rawValue.forEach((entry) => {
				formData.append(key, toFormDataEntryValue(entry));
			});
			continue;
		}

		formData.append(key, toFormDataEntryValue(rawValue));
	}
};

/**
 * SATOS portal API operations - *INTERNAL* Use SATOSPortal for operations
 */
export class DataAdapter {
	private static async createHttpError(response: Response): Promise<HttpError> {
		const baseMessage = `HTTP ${response.status}`;
		const contentType = response.headers.get("content-type") ?? "";
		const fallbackMessage = response.statusText?.trim();

		try {
			if (contentType.includes("application/json")) {
				const payload = (await response.json()) as Record<string, unknown>;
				const candidates = [
					payload.message,
					payload.error,
					payload.title,
					payload.detail,
				];
				const detail = candidates.find(
					(value): value is string =>
						typeof value === "string" && value.trim().length > 0,
				);
				if (detail) {
					return new HttpError(response.status, `${baseMessage}: ${detail}`);
				}
			} else {
				const bodyText = (await response.text()).trim();
				if (bodyText.length > 0) {
					return new HttpError(response.status, `${baseMessage}: ${bodyText}`);
				}
			}
		} catch {
			// Ignore body parsing errors and use fallback message below.
		}

		return new HttpError(
			response.status,
			fallbackMessage ? `${baseMessage}: ${fallbackMessage}` : baseMessage,
		);
	}

	private static async ensureSuccessfulResponse(
		response: Response,
	): Promise<void> {
		if (response.ok) return;

		throw await this.createHttpError(response);
	}

	private static async parseJsonResponse<T>(response: Response): Promise<T> {
		if (response.status === 204) {
			return undefined as T;
		}

		const body = await response.text();
		if (!body) {
			return undefined as T;
		}

		let result: unknown;
		try {
			result = JSON.parse(body);
		} catch {
			return body as T;
		}

		if (isODataResult(result)) {
			return result.value as T;
		}
		return result as T;
	}

	/**
	 * GET data from the SATOS portal API.
	 *
	 * @param options Request options including endpoint and query/OData params.
	 */
	public static async Get<T>(options: NamedParameters): Promise<T> {
		let params = "";

		if (options.queryParams) {
			params = `${options.queryParams}`;
		}

		if (options.odataParams) {
			params += options.odataParams;
		}

		const url = `${this.ResolveApiUrl()}/${options.endpoint}${params ? "/" + params : ""}`;
		const response = await fetch(url, {
			credentials: "include",
			mode: "cors",
			signal: options.signal,
		});

		await this.ensureSuccessfulResponse(response);
		return await this.parseJsonResponse<T>(response);
	}

	public static async Post<T>(endpoint: string, payload: unknown): Promise<T> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include", // include, *same-origin, omit
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload), // body data type must match "Content-Type" header
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);
		await this.ensureSuccessfulResponse(response);

		if (response.status === 204 || response.status === 201) {
			return {} as T;
		}
		return (await response.json()) as T;
	}

	public static async PostWithNoResponseBody(
		endpoint: string,
		payload: unknown,
	): Promise<void> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include", // include, *same-origin, omit
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload), // body data type must match "Content-Type" header
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);
	}

	// Overload for sending payload with DELETE request
	public static async Delete<T>(endpoint: string, payload: unknown): Promise<T>;

	// Overload for DELETE request without payload
	public static async Delete(endpoint: string): Promise<void>;

	public static async Delete<T>(endpoint: string, payload?: unknown) {
		if (payload !== undefined) {
			const options: RequestInit = {
				method: "DELETE",
				mode: "cors",
				credentials: "include", // include, *same-origin, omit
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload), // body data type must match "Content-Type" header
			};
			const response = await fetch(
				`${this.ResolveApiUrl()}/${endpoint}`,
				options,
			);

			await this.ensureSuccessfulResponse(response);

			if (response.status === 204) {
				return {} as T;
			}

			return;
		} else {
			const options: RequestInit = {
				method: "DELETE",
				mode: "cors",
				credentials: "include", // include, *same-origin, omit
			};

			const response = await fetch(
				`${this.ResolveApiUrl()}/${endpoint}`,
				options,
			);

			await this.ensureSuccessfulResponse(response);
		}
	}

	public static async Put<T>(endpoint: string, payload: unknown): Promise<T> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		const text = await response.text();
		if (!text) {
			return {} as T;
		}
		return JSON.parse(text) as T;
	}

	public static async PutWithNoResponseBody(
		endpoint: string,
		payload: unknown,
	): Promise<void> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);
	}

	public static async PutWithNoPayload(endpoint: string) {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);
	}

	public static async Patch<T>(endpoint: string, payload: unknown): Promise<T> {
		const options: RequestInit = {
			method: "PATCH",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		if (response.status === 204) {
			return {} as T;
		}

		const text = await response.text();
		if (!text) {
			return {} as T;
		}
		return JSON.parse(text) as T;
	}

	public static async PatchWithNoPayload(endpoint: string) {
		const options: RequestInit = {
			method: "PATCH",
			mode: "cors",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
			},
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);
	}

	public static async UploadForm<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = new FormData();
		appendFormPayload(formData, payload, "indexed");

		return await this.Upload<T>(endpoint, formData);
	}

	public static async UploadFormPatch<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = new FormData();
		appendFormPayload(formData, payload);

		return await this.UploadPatch<T>(endpoint, formData);
	}

	public static async PutForm<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = new FormData();
		appendFormPayload(formData, payload);

		return await this.UploadPut<T>(endpoint, formData);
	}

	public static async PutFormObject<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = serialize(payload, {
			indices: false,
			booleansAsIntegers: false,
		});

		return await this.UploadPut<T>(endpoint, formData);
	}

	public static async PutUploadForm<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = new FormData();
		appendFormPayload(formData, payload, "indexed");

		return await this.PutUpload<T>(endpoint, formData);
	}

	public static async PutUploadFormMedia<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		return await this.PutUpload<T>(endpoint, payload);
	}

	public static async AddUploadFormMedia<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		return await this.AddUpload<T>(endpoint, payload);
	}

	public static async Upload<T>(
		endpoint: string,
		payload: BodyInit,
	): Promise<T> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		return await this.parseJsonResponse<T>(response);
	}

	public static async UploadPut<T>(
		endpoint: string,
		payload: BodyInit,
	): Promise<T> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		// Check if response body is empty
		const responseBody = await response.text();
		if (!responseBody) {
			return {} as T; // Return an empty object if the response body is empty
		}

		return JSON.parse(responseBody) as T;
	}

	public static async UploadPatch<T>(
		endpoint: string,
		payload: BodyInit,
	): Promise<T> {
		const options: RequestInit = {
			method: "PATCH",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);
		await this.ensureSuccessfulResponse(response);

		return (await response.json()) as T;
	}

	public static async UploadImage<T>(
		endpoint: string,
		payload: File,
	): Promise<T> {
		const formData = new FormData();

		if (payload) {
			formData.append("Image", payload);
		}

		return await this.PutUpload<T>(endpoint, formData);
	}

	public static async PutUpload<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		const options: RequestInit = {
			method: "PUT",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		const responseBody = await response.text();
		if (!responseBody) {
			return {} as T; // Return an empty object if the response body is empty
		}

		return JSON.parse(responseBody) as T;
	}

	public static async AddUpload<T>(
		endpoint: string,
		payload: FormData,
	): Promise<T> {
		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: payload,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		const responseBody = await response.text();
		if (!responseBody) {
			return {} as T; // Return an empty object if the response body is empty
		}

		return JSON.parse(responseBody) as T;
	}
	public static async PostForm<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = new FormData();
		appendFormPayload(formData, payload);

		const options: RequestInit = {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: formData,
		};

		const response = await fetch(
			`${this.ResolveApiUrl()}/${endpoint}`,
			options,
		);

		await this.ensureSuccessfulResponse(response);

		return (await response.json()) as T;
	}

	public static async PostFormObject<T>(
		endpoint: string,
		payload: FormPayload,
	): Promise<T> {
		const formData = serialize(payload, {
			indices: false,
			booleansAsIntegers: true,
		});

		const response = await fetch(`${this.ResolveApiUrl()}/${endpoint}`, {
			method: "POST",
			mode: "cors",
			credentials: "include",
			body: formData,
		});

		await this.ensureSuccessfulResponse(response);

		return (await response.json()) as T;
	}

	private static ResolveApiUrl() {
		if (import.meta.env.DEV) return "/api";
		if (typeof window === "undefined") return "/api";

		return `${window.location.origin}/api`;
	}
}
